import { GPUResourceUsageFlags } from '@zephyr3d/device';
import { WebGLStructuredBuffer } from './structuredbuffer_webgl.js';
import { WebGLGPUObject } from './gpuobject_webgl.js';
import { WebGLGPUBuffer } from './buffer_webgl.js';

class WebGLBindGroup extends WebGLGPUObject {
    _layout;
    _dynamicOffsets;
    _resources;
    _createdBuffers;
    constructor(device, layout){
        super(device);
        this._device = device;
        this._layout = layout;
        this._dynamicOffsets = null;
        this._resources = {};
        this._object = {};
        this._createdBuffers = [];
        for (const entry of this._layout.entries){
            if (entry.buffer && entry.buffer.hasDynamicOffset) {
                if (!this._dynamicOffsets) {
                    this._dynamicOffsets = [];
                }
                this._dynamicOffsets[entry.buffer.dynamicOffsetIndex] = 0;
            }
        }
    }
    getGPUId() {
        return String(this._uid);
    }
    getLayout() {
        return this._layout;
    }
    getBuffer(name, nocreate = true) {
        return this._getBuffer(name, nocreate);
    }
    getDynamicOffsets() {
        return this._dynamicOffsets;
    }
    setBuffer(name, buffer, offset, _bindOffset, _bindSize) {
        const bindName = this._layout.nameMap?.[name] ?? name;
        for (const entry of this._layout.entries){
            if (entry.name === bindName) {
                if (!entry.buffer) {
                    console.error(`setBuffer() failed: resource '${name}' is not buffer`);
                } else {
                    if (buffer && !(buffer.usage & GPUResourceUsageFlags.BF_UNIFORM)) {
                        console.error(`setBuffer() failed: buffer resource '${name}' must be type '${entry.buffer.type}'`);
                    } else if (buffer !== this._resources[entry.name]) {
                        this._resources[entry.name] = buffer;
                    }
                    if (entry.buffer.hasDynamicOffset) {
                        this._dynamicOffsets[entry.buffer.dynamicOffsetIndex] = offset ?? 0;
                    }
                }
                return;
            }
        }
        console.error(`setBuffer() failed: no buffer resource named '${name}'`);
    }
    setRawData(name, byteOffset, data, srcPos, srcLength) {
        const mappedName = this._layout.nameMap?.[name];
        if (mappedName) {
            this.setRawData(mappedName, byteOffset, data, srcPos, srcLength);
        } else {
            const buffer = this._getBuffer(name, false);
            if (buffer) {
                buffer.bufferSubData(byteOffset, data, srcPos, srcLength);
            } else {
                console.error(`set(): no uniform buffer named '${name}'`);
            }
        }
    }
    setValue(name, value) {
        const mappedName = this._layout.nameMap?.[name];
        if (mappedName) {
            this.setValue(mappedName, {
                [name]: value
            });
        } else {
            const buffer = this._getBuffer(name, false);
            if (buffer) {
                if (!(buffer instanceof WebGLStructuredBuffer)) {
                    throw new Error(`BindGroup.setValue() failed: '${name}' is not structured buffer`);
                }
                if (typeof value === 'number') {
                    throw new Error(`BindGroup.setValue() failed: cannot set ${value} to '${name}'`);
                }
                if ('BYTES_PER_ELEMENT' in value) {
                    buffer.bufferSubData(0, value);
                } else {
                    for(const k in value){
                        buffer.set(k, value[k]);
                    }
                }
            } else {
                console.error(`set(): no uniform buffer named '${name}'`);
            }
        }
    }
    setTextureView(_name, _value, _level, _face, _mipCount, _sampler) {
        throw new Error('setTextureView() not supported for webgl device');
    }
    getTexture(name) {
        const entry = this._findTextureLayout(name);
        if (entry) {
            return this._resources[name]?.[0] || null;
        } else {
            throw new Error(`getTexture() failed:${name} is not a texture`);
        }
    }
    setTexture(name, texture, sampler) {
        const entry = this._findTextureLayout(name);
        if (entry) {
            this._resources[name] = [
                texture,
                sampler || texture.getDefaultSampler(!!entry.texture?.autoBindSamplerComparison)
            ];
        } else {
            console.error(`setTexture() failed: no texture uniform named '${name}'`);
        }
    }
    setSampler(_name, _value) {
    // no sampler uniform support for webgl
    }
    apply(program, offsets) {
        const webgl2 = this._device.isWebGL2;
        const dynamicOffsets = offsets ?? this.getDynamicOffsets();
        for(let i = 0; i < this._layout.entries.length; i++){
            const entry = this._layout.entries[i];
            const res = this._resources[entry.name];
            if (res instanceof WebGLGPUBuffer) {
                if (webgl2) {
                    if (entry.buffer.hasDynamicOffset) {
                        program.setBlock(entry.type.structName, res, dynamicOffsets[entry.buffer.dynamicOffsetIndex]);
                    } else {
                        program.setBlock(entry.type.structName, res, 0);
                    }
                } else if (res instanceof WebGLStructuredBuffer) {
                    program.setUniform(entry.name, res.getUniformData().uniforms);
                }
            } else if (Array.isArray(res)) {
                if (res[0].isTextureVideo()) {
                    res[0].updateVideoFrame();
                }
                // res[0].sampler = res[1];
                program.setUniform(entry.name, res);
            }
        }
    }
    destroy() {
        this._resources = {};
        this._object = null;
        for (const buffer of this._createdBuffers){
            buffer.dispose();
        }
        this._createdBuffers = [];
    }
    restore() {
        this._object = {};
    }
    isBindGroup() {
        return true;
    }
    _getBuffer(name, nocreate = false) {
        const bindName = this._layout.nameMap?.[name] ?? name;
        for (const entry of this._layout.entries){
            if (entry.buffer && entry.name === bindName) {
                let buffer = this._resources[entry.name];
                if (!buffer && !nocreate) {
                    buffer = this._device.createStructuredBuffer(entry.type, {
                        usage: 'uniform'
                    });
                    this._resources[entry.name] = buffer;
                    this._createdBuffers.push(buffer);
                }
                return buffer;
            }
        }
        return null;
    }
    _findTextureLayout(name) {
        for (const entry of this._layout.entries){
            if ((entry.texture || entry.storageTexture || entry.externalTexture) && entry.name === name) {
                return entry;
            }
        }
        return null;
    }
}

export { WebGLBindGroup };
//# sourceMappingURL=bindgroup_webgl.js.map
